<template>
    <div >
        
        <div v-if="isLoading">Loading...</div>
        <div v-else-if="error">{{ error }}</div>
        <div v-else-if="order">
            <h1>Payment Complete</h1>
            <div class="my-3" v-if="order.orderStatus != 'completed'">
                <OrderUpdates :orderId="orderId" :paymentSent="true" :vendorId="order.vendorId" />
            </div>
            <div class="basket-container my-3">
                <h3 class="basket-title">Your Order</h3>

                <div v-for="item in order.items" :key="item.itemId" class="basket-item">
                    <div class="basket-item-header">
                        <div>
                            <strong class="item-name">{{ item.itemName }}</strong>
                            <div class="item-qty">Qty: {{ item.quantity }}</div>
                        </div>
                    </div>

                    <!-- Options (if stored in selectedOptionsJson) -->
                    <div v-if="item.selectedOptionsJson" class="basket-options">
                        <div v-for="(opt, index) in parseOptions(item.selectedOptionsJson)" :key="index" class="basket-option-line">
                            <span>
                                {{ opt.Name }}:
                                <strong>{{ opt.Choice }}</strong>
                                <span v-if="opt.PriceAdjustment > 0">
                                    (+&pound;{{ opt.PriceAdjustment.toFixed(2) }})
                                </span>
                                <span v-else-if="opt.PriceAdjustment < 0">
                                    (-&pound;{{ Math.abs(opt.PriceAdjustment).toFixed(2) }})
                                </span>
                            </span>
                        </div>
                    </div>

                    <!-- Dietary Tags -->
                    <div v-if="item.selectedDietaryRequirementsJson" class="basket-options text-info">
                        <div v-for="(tag, i) in parseDietary(item.selectedDietaryRequirementsJson)" :key="i" class="basket-option-line">
                            <i class="fa fa-leaf me-1"></i>{{ tag }}
                        </div>
                    </div>

                    <div class="basket-price">
                        &pound;{{ calculateItemTotal(item).toFixed(2) }}
                    </div>
                </div>


                <div class="basket-total">
                    <div>Items Total: &pound;{{ itemSubtotal.toFixed(2) }}</div>
                    <div v-if="deliveryFee > 0">Delivery Fee: &pound;{{ deliveryFee.toFixed(2) }}</div>
                    <div v-else class="free-delivery">Free Delivery</div>
                    <hr />
                    <div><strong>Total: &pound;{{ order.totalAmount.toFixed(2) }}</strong></div>
                </div>
            </div>

            <div class="content checkout-address my-3" v-if="!order.collection">
                <h2>Delivery Address</h2>
                <div>Street Address: {{ order.address }}</div>
                <div>City: {{ order.city }}</div>
                <div>Postcode: {{ order.postcode }}</div>
            </div>

            <div  class="content my-3"  v-if="order.customerNotes">
                <h2>Notes</h2>
                {{order.customerNotes}}
            </div>
            
        </div>
    </div>

</template>

<script setup>
    import { storeToRefs } from 'pinia';
   
    import OrderUpdates from './OrderUpdates.vue'

    import { useOrderStore } from '../../utils/orderStore';

    import { ref, onMounted, computed } from 'vue'
    import { useRoute } from 'vue-router'
    import { fetchWithAuth } from '../../utils/authFetch'

    const route = useRoute()
    const orderId = Number(route.params.orderId)

    const order = ref(null)
    const isLoading = ref(true)
    const error = ref(null)
    const orderStore = useOrderStore();
    const {
        currentVendorItems,
        totalAmount,
        actualDeliveryFee,
        vendor,
        address,
        showCheckout
    } = storeToRefs(orderStore);

    const itemSubtotal = computed(() =>
        order.value?.items?.reduce((sum, item) => sum + calculateItemTotal(item), 0) || 0
    )
    function parseOptions(json) {
        try {
            return JSON.parse(json || '[]');
        } catch {
            return [];
        }
    }

    function parseDietary(json) {
        try {
            return JSON.parse(json || '[]');
        } catch {
            return [];
        }
    }

    // deliveryFee: total - itemSubtotal
    const deliveryFee = computed(() =>
        order.value ? order.value.totalAmount - itemSubtotal.value : 0
    )

    onMounted(async () => {
        try {
            const res = await fetchWithAuth(`/publicorder/get-user-order?id=${orderId}`)
            if (!res.ok) throw new Error('Failed to fetch order')
            order.value = await res.json()
        } catch (err) {
            error.value = err.message
        } finally {
            isLoading.value = false
        }
        orderStore.clear();

    })

    function calculateItemTotal(item) {
        const basePrice = item.price;

        let extras = 0;
        if (item.selectedOptionsJson) {
            try {
                const parsed = JSON.parse(item.selectedOptionsJson);
                extras = parsed.reduce((sum, opt) => sum + (opt.PriceAdjustment || 0), 0);
            } catch { }
        }

        return (basePrice + extras) * item.quantity;
    }

</script>
<style scoped>

    .basket-container {
        background-color: rgba(255, 255, 255, 0.05);
        background: #410330;
        padding: 1.5rem;
        border-radius: 12px;
        color: #1a1a1a;
        margin-top: 2rem;
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
        color: white;
    }

    .basket-title {
        font-size: 1.5rem;
        font-weight: 600;
        margin-bottom: 1.25rem;
    }

    .basket-item {
        border-bottom: 1px solid rgba(255, 255, 255, 0.15);
        box-shadow: 0, 0, 0, white;
        padding: .75rem 0;
        margin-bottom: 15px;
    }

        .basket-item:last-child {
            border-bottom: none;
        }

    .basket-item-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
    }

    .item-name {
        font-weight: bold;
        font-size: 1rem;
    }

    .item-qty {
        font-size: 0.85rem;
    }

    .remove-button {
        background: none;
        border: none;
        color: #e74c3c;
        font-size: 1.3rem;
        font-weight: bold;
        cursor: pointer;
        padding: 0 10px 0 0;
        margin-left: auto;
        transition: transform 0.2s ease;
    }

        .remove-button:hover {
            transform: scale(1.1);
            color: #c0392b;
        }

    .basket-options {
        font-size: 0.9rem;
        margin-top: 0.5rem;
        padding-left: 1rem;
    }

    .basket-option-line {
        margin-bottom: 0.25rem;
    }

    .basket-price {
        text-align: right;
        font-weight: bold;
        margin-top: 0.5rem;
        font-size: 1rem;
    }

    .basket-total {
        margin-top: 1.5rem;
        text-align: right;
        font-size: 1rem;
    }

        .basket-total strong {
            font-size: 1.1rem;
        }

    .free-delivery {
        color: #2ecc71;
        font-weight: bold;
    }

    .form-input {
        display: block;
        width: 100%;
        margin-bottom: 1rem;
        padding: 0.75rem;
        font-size: 1rem;
        border: 1px solid #ccc;
        border-radius: 6px;
        background: #fff;
        box-sizing: border-box;
    }
</style>